///
/// EarthBrowser Admin Dashboard - Roles & Permissions Section
///
/// Created: 2025-11-27
/// Copyright (c) 2025 Geodata Labs
///

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  useGetPermissionsQuery,
  useGetRolesQuery,
  type Permission,
  type Role,
} from "@/redux/api/permissionsApi"
import { ChevronRight, Eye, Lock, Shield, Users } from "lucide-react"
import { useState } from "react"

export function RolesPermissionsSection() {
  const { data: rolesData, isLoading: rolesLoading } = useGetRolesQuery()
  const { data: permissionsData, isLoading: permissionsLoading } = useGetPermissionsQuery()
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false)

  const handleViewRole = (role: Role) => {
    setSelectedRole(role)
    setIsRoleDialogOpen(true)
  }

  const getRolePermissions = (roleName: string): Permission[] => {
    if (!permissionsData) return []

    const rolePermissionMap: Record<string, string[]> = {
      super_admin: permissionsData.permissions.map(p => p.name),
      admin: [
        "user.read", "user.update", "user.delete", "user.create", "user.admin",
        "admin.access", "admin.users", "admin.roles", "admin.audit",
        "api.basic", "subscription.manage", "subscription.cancel",
        "device.register", "device.revoke", "device.trust", "export.data"
      ],
      moderator: [
        "user.read", "user.update", "user.delete",
        "admin.access",
        "api.basic", "subscription.manage", "subscription.cancel",
        "device.register", "device.revoke", "export.data"
      ],
      user: [
        "user.read", "user.update", "user.delete",
        "api.basic", "subscription.manage", "subscription.cancel",
        "device.register", "device.revoke", "export.data"
      ],
      guest: ["user.read"]
    }

    const permissionNames = rolePermissionMap[roleName] || []
    return permissionsData.permissions.filter(p => permissionNames.includes(p.name))
  }

  const getPriorityColor = (priority: number) => {
    if (priority >= 1000) return "bg-red-500/10 text-red-700 dark:text-red-400"
    if (priority >= 900) return "bg-orange-500/10 text-orange-700 dark:text-orange-400"
    if (priority >= 500) return "bg-amber-500/10 text-amber-700 dark:text-amber-400"
    if (priority >= 100) return "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
    return "bg-slate-500/10 text-slate-600 dark:text-slate-400"
  }

  if (rolesLoading || permissionsLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  const roles = rolesData?.roles || []
  const permissionsByResource = permissionsData?.grouped || {}

  return (
    <div className="space-y-6">
      <Tabs defaultValue="roles" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="roles">Roles Overview</TabsTrigger>
          <TabsTrigger value="permissions">All Permissions</TabsTrigger>
        </TabsList>

        <TabsContent value="roles" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">System Roles</h3>
              <p className="text-sm text-muted-foreground">
                {roles.length} role{roles.length !== 1 ? 's' : ''} defined in the system
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {roles.map((role) => (
              <Card key={role.id} className="rounded-xl border border-border bg-background/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
                <CardHeader className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <CardTitle className="text-lg">{role.name}</CardTitle>
                        {role.is_system && (
                          <Badge variant="outline" className="text-xs rounded-full">
                            <Lock className="h-3 w-3 mr-1" />
                            System
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="text-sm">
                        {role.description || "No description available"}
                      </CardDescription>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <Badge className={`${getPriorityColor(role.priority)} rounded-full`}>
                      Priority: {role.priority}
                    </Badge>
                    <Badge variant="secondary" className="rounded-full">
                      {role.permission_count || 0} permissions
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500"
                    onClick={() => handleViewRole(role)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                    <ChevronRight className="h-4 w-4 ml-auto" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="permissions" className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold">All Permissions</h3>
              <p className="text-sm text-muted-foreground">
                {permissionsData?.total || 0} permission{permissionsData?.total !== 1 ? 's' : ''} grouped by resource
              </p>
            </div>
          </div>

          <div className="grid gap-4">
            {Object.entries(permissionsByResource).map(([resource, permissions]) => (
              <Card key={resource} className="rounded-xl border border-border bg-background/50 backdrop-blur-sm transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    <CardTitle className="text-lg capitalize">{resource}</CardTitle>
                    <Badge variant="secondary" className="rounded-full">{permissions.length}</Badge>
                  </div>
                  <CardDescription>
                    Permissions related to {resource} resources
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {permissions.map((permission) => (
                      <div
                        key={permission.id}
                        className="flex items-start gap-2 p-3 rounded-xl border bg-card hover:bg-accent/50 transition-all duration-300"
                      >
                        <Lock className="h-4 w-4 mt-0.5 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-sm font-medium truncate">
                            {permission.name}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {permission.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <DialogTitle className="text-2xl">{selectedRole?.name}</DialogTitle>
              {selectedRole?.is_system && (
                <Badge variant="outline" className="rounded-full">
                  <Lock className="h-3 w-3 mr-1" />
                  System Role
                </Badge>
              )}
            </div>
            <DialogDescription className="text-base">
              {selectedRole?.description || "No description available"}
            </DialogDescription>
          </DialogHeader>

          {selectedRole && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-xl">
                <div>
                  <p className="text-sm text-muted-foreground">Priority</p>
                  <Badge className={`${getPriorityColor(selectedRole.priority)} rounded-full`} >
                    {selectedRole.priority}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Permissions</p>
                  <p className="text-lg font-semibold">
                    {getRolePermissions(selectedRole.name).length}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Included Permissions
                </h4>
                <div className="grid gap-2">
                  {getRolePermissions(selectedRole.name).length > 0 ? (
                    getRolePermissions(selectedRole.name).map((permission) => (
                      <div
                        key={permission.id}
                        className="flex items-start gap-3 p-3 rounded-xl border bg-card"
                      >
                        <Lock className="h-4 w-4 mt-0.5 text-muted-foreground" />
                        <div className="flex-1">
                          <p className="font-mono text-sm font-medium">
                            {permission.name}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {permission.description}
                          </p>
                          <div className="flex gap-2 mt-2">
                            <Badge variant="outline" className="text-xs rounded-full">
                              {permission.resource}
                            </Badge>
                            <Badge variant="outline" className="text-xs rounded-full">
                              {permission.action}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground italic p-4 text-center">
                      No permissions assigned to this role
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

